export const MAX_PREVIEW_ENTRIES = 64
export const MAX_ENTRY_PREVIEW_BYTES = 512 * 1024
export const MAX_TOTAL_PREVIEW_BYTES = 2 * 1024 * 1024
const BINARY_SNIFF_BYTES = 8192

export type SkillPackageEntryBody =
  | { kind: "text", text: string }
  | { kind: "binary" }
  | { kind: "tooLarge" }
  | { kind: "budgetExhausted" }

export interface SkillPackageEntry {
  path: string
  sizeBytes: number
  body: SkillPackageEntryBody
}

export interface SkillPackageContents {
  entries: SkillPackageEntry[]
  omittedEntryCount: number
}

import { basenameOf, directoryOf } from "./skill-package-paths"

const SKILL_MANIFEST_PATH = "skill.md"

export function isSkillManifestPath(path: string): boolean {
  return path.toLowerCase() === SKILL_MANIFEST_PATH
}

function base64ToBytes(base64: string): Uint8Array {
  const binary = atob(base64)
  const bytes = new Uint8Array(binary.length)
  for (let index = 0; index < binary.length; index += 1) bytes[index] = binary.charCodeAt(index)
  return bytes
}

export function declaredUncompressedSize(file: unknown): number | null {
  const data = (file as { _data?: { uncompressedSize?: unknown } })._data
  const size = data?.uncompressedSize
  return typeof size === "number" && Number.isFinite(size) && size >= 0 ? size : null
}

function isBinary(bytes: Uint8Array): boolean {
  const limit = Math.min(bytes.length, BINARY_SNIFF_BYTES)
  for (let index = 0; index < limit; index += 1) {
    if (bytes[index] === 0) return true
  }
  return false
}

function byPreviewOrder(left: string, right: string): number {
  const leftIsManifest = isSkillManifestPath(left)
  if (leftIsManifest !== isSkillManifestPath(right)) return leftIsManifest ? -1 : 1
  const leftDirectory = directoryOf(left)
  const rightDirectory = directoryOf(right)
  if (leftDirectory !== rightDirectory) return leftDirectory.localeCompare(rightDirectory)
  return basenameOf(left).localeCompare(basenameOf(right))
}

export async function readSkillPackage(bundle: string): Promise<SkillPackageContents> {
  const { default: JSZip } = await import("jszip")
  const zip = await JSZip.loadAsync(base64ToBytes(bundle))

  const paths: string[] = []
  zip.forEach((path, file) => {
    if (!file.dir) paths.push(path)
  })
  paths.sort(byPreviewOrder)

  const previewed = paths.slice(0, MAX_PREVIEW_ENTRIES)
  let remainingBytes = MAX_TOTAL_PREVIEW_BYTES
  const entries: SkillPackageEntry[] = []

  for (const path of previewed) {
    const file = zip.file(path)
    if (file == null) continue
    const declaredSize = declaredUncompressedSize(file)

    if (declaredSize !== null && declaredSize > MAX_ENTRY_PREVIEW_BYTES) {
      entries.push({ path, sizeBytes: declaredSize, body: { kind: "tooLarge" } })
      continue
    }
    if (declaredSize !== null && declaredSize > remainingBytes) {
      entries.push({ path, sizeBytes: declaredSize, body: { kind: "budgetExhausted" } })
      continue
    }
    if (remainingBytes <= 0) {
      entries.push({ path, sizeBytes: declaredSize ?? 0, body: { kind: "budgetExhausted" } })
      continue
    }

    const bytes = await file.async("uint8array")
    if (bytes.length > MAX_ENTRY_PREVIEW_BYTES) {
      entries.push({ path, sizeBytes: bytes.length, body: { kind: "tooLarge" } })
      continue
    }
    if (bytes.length > remainingBytes) {
      entries.push({ path, sizeBytes: bytes.length, body: { kind: "budgetExhausted" } })
      remainingBytes = 0
      continue
    }
    remainingBytes -= bytes.length
    if (isBinary(bytes)) {
      entries.push({ path, sizeBytes: bytes.length, body: { kind: "binary" } })
      continue
    }
    entries.push({ path, sizeBytes: bytes.length, body: { kind: "text", text: await file.async("string") } })
  }

  return { entries, omittedEntryCount: paths.length - previewed.length }
}
